import { useEffect, useRef } from "react";
import { useProductMember } from "../../context/ProductContext";
import { useContentSetting } from "../../Hooks/ContentSetting";
import { update_user_region_api } from "../../DAL/Login/Login";
import { PORTAL_TYPE } from "../../utils/classRoomUtils";

/**
 * Hydrate ContentSetting (userInfo, socket, unread) from ProductSession workspace
 * once member_session → member_init completes.
 */
export default function ProductWorkspaceSync() {
  const { workspace, member, ready } = useProductMember();
  const {
    setUserInfo,
    setUnreadMessageCount,
    setStartedMeeting,
    setInitSettings,
    START_SOCKET_FORCEFULLY,
    userInfo,
  } = useContentSetting();
  const regionSyncedRef = useRef(false);
  const socketStartedForRef = useRef(null);

  useEffect(() => {
    if (!ready || !workspace?.admin_user) return;

    const adminUser = workspace.admin_user;
    setUserInfo(adminUser);
    setUnreadMessageCount(workspace.unread_message_count ?? 0);
    setStartedMeeting(workspace.started_call ?? null);
    setInitSettings(workspace);

    const id = adminUser._id || adminUser.id;
    if (id && socketStartedForRef.current !== id) {
      socketStartedForRef.current = id;
      START_SOCKET_FORCEFULLY(id);
    }
  }, [
    ready,
    workspace,
    setUserInfo,
    setUnreadMessageCount,
    setStartedMeeting,
    setInitSettings,
    START_SOCKET_FORCEFULLY,
  ]);

  useEffect(() => {
    if (!ready || !member || regionSyncedRef.current) return;
    if (userInfo?.user_region) {
      regionSyncedRef.current = true;
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(
          "https://ipinfo.io/json?token=702699ce736041",
        );
        const region_data = await response.json();
        if (cancelled) return;
        const { language, country } = region_data;
        if (!language && country) {
          const locale = new Intl.Locale("und", { region: country });
          region_data.language = locale.maximize().language;
        }
        region_data.country_lang = `${region_data.language}-${country}`;
        setUserInfo((old) => ({ ...old, user_region: region_data }));
        await update_user_region_api({
          user_region: region_data,
          type: PORTAL_TYPE,
        });
        regionSyncedRef.current = true;
      } catch {
        /* best-effort region sync */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [ready, member, userInfo?.user_region, setUserInfo]);

  return null;
}
